import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  cloneExecutionColumnOptions,
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
  cloneExecutionSortOptions,
  cloneExecutionStatusOptions,
} from '@/features/tests/pages/testExecutionsTableDefinition';
import {
  type CloneSortDirection,
  type CloneSortKey,
  testsTableDefinition,
} from '@/features/tests/pages/testsTableDefinition';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Panel } from '@/ui/composites/Panel';
import { Box, Tab, Tabs, type GridColumnVisibilityModel } from '@/ui/primitives';
import { TestExecutionsTable } from '../components/TestExecutionsTable';
import { TestsTable } from '../components/TestsTable';
import type { TestStatusKey } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  TestExecutionDto,
  TestExecutionStatus,
  TestListItemDto,
} from '@flowqa/shared';
import type { Dayjs } from 'dayjs';
import type { Dispatch, ReactElement, SetStateAction } from 'react';

type CloneTestsPanelTab = 'tests' | 'executions';

type TestsPageClonePanelProps = {
  cloneActiveTab: CloneTestsPanelTab;
  setCloneActiveTab: Dispatch<SetStateAction<CloneTestsPanelTab>>;
  cloneDateQuickFilter: '7d' | '14d' | '30d' | 'latest';
  setCloneDateQuickFilter: Dispatch<SetStateAction<'7d' | '14d' | '30d' | 'latest'>>;
  cloneFromDate: Dayjs | null;
  setCloneFromDate: Dispatch<SetStateAction<Dayjs | null>>;
  cloneToDate: Dayjs | null;
  setCloneToDate: Dispatch<SetStateAction<Dayjs | null>>;
  cloneColumnVisibilityModel: GridColumnVisibilityModel;
  setCloneColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  cloneSortKey: CloneSortKey;
  setCloneSortKey: Dispatch<SetStateAction<CloneSortKey>>;
  cloneSortDirection: CloneSortDirection;
  setCloneSortDirection: Dispatch<SetStateAction<CloneSortDirection>>;
  cloneFilterStatuses: TestExecutionStatus[];
  setCloneFilterStatuses: Dispatch<SetStateAction<TestExecutionStatus[]>>;
  cloneFilterTests: string[];
  setCloneFilterTests: Dispatch<SetStateAction<string[]>>;
  cloneFilterSuites: string[];
  setCloneFilterSuites: Dispatch<SetStateAction<string[]>>;
  cloneFilterTags: string[];
  setCloneFilterTags: Dispatch<SetStateAction<string[]>>;
  cloneExecutionColumnVisibilityModel: GridColumnVisibilityModel;
  setCloneExecutionColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  cloneExecutionSortKey: CloneExecutionSortKey;
  setCloneExecutionSortKey: Dispatch<SetStateAction<CloneExecutionSortKey>>;
  cloneExecutionSortDirection: CloneExecutionSortDirection;
  setCloneExecutionSortDirection: Dispatch<SetStateAction<CloneExecutionSortDirection>>;
  cloneExecutionFilterStatuses: TestExecutionStatus[];
  setCloneExecutionFilterStatuses: Dispatch<SetStateAction<TestExecutionStatus[]>>;
  cloneExecutionFilterSuites: string[];
  setCloneExecutionFilterSuites: Dispatch<SetStateAction<string[]>>;
  cloneExecutionFilterTags: string[];
  setCloneExecutionFilterTags: Dispatch<SetStateAction<string[]>>;
  cloneAvailableTags: string[];
  cloneExecutionAvailableTags: string[];
  suiteOptions: Array<{ value: string; label: string }>;
  testOptions: Array<{ value: string; label: string }>;
  cloneDateWindow: { startDay: string | null; endDay: string | null; sparklineRangeDays: number };
  cloneStatusFilterKey: string;
  cloneTestFilterKey: string;
  cloneSuiteFilterKey: string;
  cloneTagFilterKey: string;
  cloneExecutionStatusFilterKey: string;
  cloneExecutionSuiteFilterKey: string;
  cloneExecutionTagFilterKey: string;
  cloneNoRowsText: string;
  cloneExecutionsNoRowsText: string;
  debouncedCloneSearch: string;
  debouncedCloneExecutionSearch: string;
  cloneTestsMeta: TableQueryMeta<TestListItemDto> | null;
  setCloneTestsMeta: Dispatch<SetStateAction<TableQueryMeta<TestListItemDto> | null>>;
  cloneTestExecutionsMeta: TableQueryMeta<TestExecutionDto> | null;
  setCloneTestExecutionsMeta: Dispatch<SetStateAction<TableQueryMeta<TestExecutionDto> | null>>;
  cloneTestExecutionDateBounds: { from: Dayjs | null; to: Dayjs | null };
  showCloneDeltas: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  handleSearchChange: (value: string) => void;
  handleRangeChange: (value: '7d' | '14d' | '30d' | 'latest') => void;
  handleSelectedStartDayChange: (value: string | null) => void;
  handleSelectedEndDayChange: (value: string | null) => void;
  handleStatusesChange: (value: TestStatusKey[]) => void;
  handleTagsChange: (value: string[]) => void;
  handleDebouncedCloneSearchChange: (value: string) => void;
  handleDebouncedCloneExecutionSearchChange: (value: string) => void;
  cloneQueryFn: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  cloneExecutionQueryFn: (input: GridQueryInput) => Promise<QueryResult<TestExecutionDto>>;
};

export const TestsPageClonePanel = ({
  cloneActiveTab,
  setCloneActiveTab,
  cloneDateQuickFilter,
  setCloneDateQuickFilter,
  cloneFromDate,
  setCloneFromDate,
  cloneToDate,
  setCloneToDate,
  cloneColumnVisibilityModel,
  setCloneColumnVisibilityModel,
  cloneSortKey,
  setCloneSortKey,
  cloneSortDirection,
  setCloneSortDirection,
  cloneFilterStatuses,
  setCloneFilterStatuses,
  cloneFilterTests,
  setCloneFilterTests,
  cloneFilterSuites,
  setCloneFilterSuites,
  cloneFilterTags,
  setCloneFilterTags,
  cloneExecutionColumnVisibilityModel,
  setCloneExecutionColumnVisibilityModel,
  cloneExecutionSortKey,
  setCloneExecutionSortKey,
  cloneExecutionSortDirection,
  setCloneExecutionSortDirection,
  cloneExecutionFilterStatuses,
  setCloneExecutionFilterStatuses,
  cloneExecutionFilterSuites,
  setCloneExecutionFilterSuites,
  cloneExecutionFilterTags,
  setCloneExecutionFilterTags,
  cloneAvailableTags,
  cloneExecutionAvailableTags,
  suiteOptions,
  testOptions,
  cloneDateWindow,
  cloneStatusFilterKey,
  cloneTestFilterKey,
  cloneSuiteFilterKey,
  cloneTagFilterKey,
  cloneExecutionStatusFilterKey,
  cloneExecutionSuiteFilterKey,
  cloneExecutionTagFilterKey,
  cloneNoRowsText,
  cloneExecutionsNoRowsText,
  debouncedCloneSearch,
  debouncedCloneExecutionSearch,
  cloneTestsMeta,
  setCloneTestsMeta,
  cloneTestExecutionsMeta,
  setCloneTestExecutionsMeta,
  cloneTestExecutionDateBounds,
  showCloneDeltas,
  selectedOrganisationId,
  selectedProjectId,
  handleSearchChange,
  handleRangeChange,
  handleSelectedStartDayChange,
  handleSelectedEndDayChange,
  handleStatusesChange,
  handleTagsChange,
  handleDebouncedCloneSearchChange,
  handleDebouncedCloneExecutionSearchChange,
  cloneQueryFn,
  cloneExecutionQueryFn,
}: TestsPageClonePanelProps): ReactElement => (
  <Panel
    padding="md"
    gap="none"
    contentGap="md"
    headerGap="none"
    headerSlot={
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="md"
          sx={{
            minWidth: 0,
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            <Tabs
              value={cloneActiveTab}
              onChange={(_event, next) => {
                const nextTab = next as CloneTestsPanelTab;
                setCloneActiveTab(nextTab);
                if (nextTab === 'tests' && cloneDateQuickFilter === 'latest') {
                  setCloneDateQuickFilter('7d');
                  handleRangeChange('7d');
                }
              }}
              aria-label="Clone tests panel tabs"
              sx={{ pl: s('sm') }}
            >
              <Tab label="Tests" value="tests" />
              <Tab label="Executions" value="executions" />
            </Tabs>
          </Box>
          {cloneActiveTab === 'tests' ? (
            <Box sx={{ flex: '0 0 auto' }}>
              <TableToolbar<CloneSortKey, TestExecutionStatus>
                title={undefined}
                searchAriaLabel={testsTableDefinition.searchAriaLabel}
                searchValue={debouncedCloneSearch}
                onDebouncedSearchChange={(value) => {
                  handleDebouncedCloneSearchChange(value);
                  handleSearchChange(value);
                }}
                columnVisibilityModel={cloneColumnVisibilityModel}
                defaultColumnVisibilityModel={testsTableDefinition.defaultColumnVisibilityModel}
                columnOptions={testsTableDefinition.columnOptions}
                onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                sortKey={cloneSortKey}
                sortDirection={cloneSortDirection}
                defaultSortKey={testsTableDefinition.defaultSortKey}
                defaultSortDirection={testsTableDefinition.defaultSortDirection}
                sortOptions={testsTableDefinition.sortOptions}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneSortKey(nextSortKey);
                  setCloneSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneFilterStatuses}
                statusOptions={testsTableDefinition.statusOptions}
                onFilterStatusesChange={(next) => {
                  setCloneFilterStatuses(next);
                  handleStatusesChange(next as TestStatusKey[]);
                }}
                filterEntities={cloneFilterTests}
                onFilterEntitiesChange={setCloneFilterTests}
                entityOptions={testOptions}
                entityLabel="Test"
                filterSuites={cloneFilterSuites}
                onFilterSuitesChange={setCloneFilterSuites}
                suiteOptions={suiteOptions}
                filterTags={cloneFilterTags}
                onFilterTagsChange={(next) => {
                  setCloneFilterTags(next);
                  handleTagsChange(next);
                }}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                  handleRangeChange(next);
                  handleSelectedStartDayChange(null);
                  handleSelectedEndDayChange(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={(next) => {
                  setCloneFromDate(next);
                  handleSelectedStartDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                onToDateChange={(next) => {
                  setCloneToDate(next);
                  handleSelectedEndDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                availableTags={cloneAvailableTags}
                summaryCount={cloneTestsMeta?.total ?? null}
                summarySingularLabel="test"
                summaryPluralLabel="tests"
              />
            </Box>
          ) : null}
          {cloneActiveTab === 'executions' ? (
            <Box sx={{ flex: '0 0 auto' }}>
              <TableToolbar<CloneExecutionSortKey, TestExecutionStatus>
                title={undefined}
                searchAriaLabel="Search test executions"
                searchValue={debouncedCloneExecutionSearch}
                onDebouncedSearchChange={(value) => {
                  handleDebouncedCloneExecutionSearchChange(value);
                  handleSearchChange(value);
                }}
                columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                defaultColumnVisibilityModel={cloneExecutionDefaultColumnVisibilityModel}
                columnOptions={cloneExecutionColumnOptions}
                onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                sortKey={cloneExecutionSortKey}
                sortDirection={cloneExecutionSortDirection}
                defaultSortKey={cloneExecutionDefaultSortKey}
                defaultSortDirection={cloneExecutionDefaultSortDirection}
                sortOptions={cloneExecutionSortOptions}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneExecutionSortKey(nextSortKey);
                  setCloneExecutionSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneExecutionFilterStatuses}
                statusOptions={cloneExecutionStatusOptions}
                onFilterStatusesChange={(next) => {
                  setCloneExecutionFilterStatuses(next);
                  handleStatusesChange(next as TestStatusKey[]);
                }}
                filterSuites={cloneExecutionFilterSuites}
                onFilterSuitesChange={setCloneExecutionFilterSuites}
                suiteOptions={suiteOptions}
                filterTags={cloneExecutionFilterTags}
                onFilterTagsChange={(next) => {
                  setCloneExecutionFilterTags(next);
                  handleTagsChange(next);
                }}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                  handleRangeChange(next);
                  handleSelectedStartDayChange(null);
                  handleSelectedEndDayChange(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={(next) => {
                  setCloneFromDate(next);
                  handleSelectedStartDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                onToDateChange={(next) => {
                  setCloneToDate(next);
                  handleSelectedEndDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                availableTags={cloneExecutionAvailableTags}
                includeLatestQuickFilter
                statusFilterLabel="Status"
                summaryCount={cloneTestExecutionsMeta?.total ?? null}
                summarySingularLabel="test execution"
                summaryPluralLabel="test executions"
                previewFromDate={
                  cloneDateQuickFilter === 'latest' ? cloneTestExecutionDateBounds.from : null
                }
                previewToDate={
                  cloneDateQuickFilter === 'latest' ? cloneTestExecutionDateBounds.to : null
                }
              />
            </Box>
          ) : null}
        </Stack>
      </Box>
    }
    sx={{ backgroundColor: 'background.paper' }}
  >
    {cloneActiveTab === 'tests' ? (
      <TestsTable
        persistKey={null}
        queryKey={[
          'tests-table',
          cloneDateQuickFilter,
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          showCloneDeltas ? 'deltas-on' : 'deltas-off',
          cloneSortKey,
          cloneSortDirection,
          cloneStatusFilterKey,
          cloneTestFilterKey,
          cloneSuiteFilterKey,
          cloneTagFilterKey,
          cloneDateWindow.startDay,
          cloneDateWindow.endDay,
        ]}
        queryFn={cloneQueryFn}
        pageSizeOptions={[5, 10, 25, 50]}
        initialPageSize={5}
        showDeltas={showCloneDeltas}
        sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
        metricsStartDay={cloneDateWindow.startDay ?? undefined}
        metricsEndDay={cloneDateWindow.endDay ?? undefined}
        noRowsText={cloneNoRowsText}
        showToolbar={false}
        disableColumnSorting
        disableColumnMenu
        search={debouncedCloneSearch}
        onQueryMeta={setCloneTestsMeta}
        columnVisibilityModel={cloneColumnVisibilityModel}
        onColumnVisibilityModelChange={(nextModel) => {
          setCloneColumnVisibilityModel({
            ...nextModel,
            name: true,
          });
        }}
      />
    ) : null}
    {cloneActiveTab === 'executions' ? (
      <TestExecutionsTable
        persistKey={null}
        queryKey={[
          'test-executions-table',
          cloneDateQuickFilter,
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          cloneExecutionSortKey,
          cloneExecutionSortDirection,
          cloneExecutionStatusFilterKey,
          cloneExecutionSuiteFilterKey,
          cloneExecutionTagFilterKey,
          debouncedCloneExecutionSearch,
          cloneDateWindow.startDay,
          cloneDateWindow.endDay,
        ]}
        queryFn={cloneExecutionQueryFn}
        pageSizeOptions={[5, 10, 25, 50]}
        initialPageSize={5}
        noRowsText={cloneExecutionsNoRowsText}
        showToolbar={false}
        disableColumnSorting
        disableColumnMenu
        search={debouncedCloneExecutionSearch}
        onQueryMeta={setCloneTestExecutionsMeta}
        columnVisibilityModel={cloneExecutionColumnVisibilityModel}
        onColumnVisibilityModelChange={(nextModel) => {
          setCloneExecutionColumnVisibilityModel({
            ...nextModel,
            name: true,
          });
        }}
      />
    ) : null}
  </Panel>
);
